import BigNumber from 'bignumber.js';

import { msToDate } from '#/utils/date';
import { Signature } from '#/features/starknet-wallet/wallet';

import { UnixTimeMs, UnixTime } from './types';
import { AsyncResp, BaseReq, requestApi } from './fetch-api';

export type AccountStatus =
  | 'ACTIVE'
  | 'DELEVERAGE'
  | 'UNHEALTHY'
  | 'LIQUIDATION';

export interface RawAccount {
  /** @example '0xbBbBBBBbbBBBbbbBbbBbbbbBBbBbbbbBbBbbBBbB' */
  readonly account: string;
  readonly status: AccountStatus;
  /** @example '0' */
  readonly account_value: string;
  /** @example '0' */
  readonly maintenance_margin_requirement: string;
  /** @example '0' */
  readonly margin_cushion: string;
  /** @example '0' */
  readonly free_collateral: string;
  /** @example '0' */
  readonly total_collateral: string;
  /** @example '0' */
  readonly portfolio_change_percentage: string;
  /** @example '0' */
  readonly portfolio_change: string;
  /** @example '0' */
  readonly initial_margin_requirement: string;
  readonly updated_at: UnixTimeMs;
  /** @example 'USDC' */
  readonly settlement_asset: string;
  /**
   * Custom username created by the user.
   * Empty string when user hasn't picked one yet.
   */
  readonly username: string;
  /** Automatically generated username */
  readonly anonymous_username: string;
  /** Whether the user wants to keep its username private */
  readonly is_username_private: boolean;
}

export interface Account
  extends Omit<
    RawAccount,
    | 'updated_at'
    | 'total_collateral'
    | 'free_collateral'
    | 'account_value'
    | 'portfolio_change'
    | 'portfolio_change_percentage'
    | 'initial_margin_requirement'
    | 'maintenance_margin_requirement'
  > {
  readonly updated_at: Date;
  readonly total_collateral: BigNumber;
  readonly free_collateral: BigNumber;
  readonly account_value: BigNumber;
  readonly portfolio_change: BigNumber;
  readonly portfolio_change_percentage: BigNumber;
  readonly initial_margin_requirement: BigNumber;
  readonly maintenance_margin_requirement: BigNumber;
}

interface GetAccountReq extends BaseReq {}

export async function getAccount(req: GetAccountReq): AsyncResp<Account> {
  const { signal } = req;

  const resp = await requestApi<RawAccount>({
    signal,
    method: 'GET',
    url: `/account`,
  });

  if (!resp.ok) return resp;

  return {
    ...resp,
    data: processAccount(resp.data),
  };
}

interface UpdateAccountReq extends BaseReq {
  readonly username: string;
  readonly is_username_private: boolean;
}

export async function updateAccount(req: UpdateAccountReq): AsyncResp<Account> {
  const { signal, ...body } = req;

  const resp = await requestApi<Account>({
    signal,
    method: 'POST',
    url: `/account`,
    body,
  });

  return resp;
}

interface PostOnboardingReq extends BaseReq {
  readonly starknetPublicKey: string;
  readonly starknetAddress: string;
  readonly ethereumAddress: string;
  readonly signature: Signature;
}

export async function postOnboarding(req: PostOnboardingReq): AsyncResp<null> {
  const {
    signal,
    starknetAddress,
    ethereumAddress,
    starknetPublicKey,
    signature,
  } = req;

  const resp = await requestApi<null>({
    signal,
    method: 'POST',
    url: `/onboarding`,
    headers: {
      'PARADEX-ETHEREUM-ACCOUNT': ethereumAddress,
      'PARADEX-STARKNET-ACCOUNT': starknetAddress,
      'PARADEX-STARKNET-SIGNATURE': JSON.stringify(signature),
    },
    body: {
      public_key: starknetPublicKey,
    },
  });

  return resp;
}

interface PostAuthReq extends BaseReq {
  readonly starknetAddress: string;
  readonly signature: Signature;
  /** Timestamp in seconds of when the request was signed */
  readonly createdAt: UnixTime;
  /** Timestamp in seconds used to change the default signature expiration time */
  readonly expiresAt: UnixTime;
}

interface PostAuthResp {
  readonly jwt_token: string;
}

export async function postAuth(req: PostAuthReq): AsyncResp<PostAuthResp> {
  const { signal, starknetAddress, signature, createdAt, expiresAt } = req;

  const resp = await requestApi<PostAuthResp>({
    signal,
    method: 'POST',
    url: `/auth`,
    headers: {
      'PARADEX-STARKNET-ACCOUNT': starknetAddress,
      'PARADEX-STARKNET-SIGNATURE': JSON.stringify(signature),
      'PARADEX-TIMESTAMP': createdAt.toFixed(),
      'PARADEX-SIGNATURE-EXPIRATION': expiresAt.toFixed(),
    },
  });

  return resp;
}

export function processAccount(account: RawAccount): Account {
  return {
    ...account,
    updated_at: msToDate(account.updated_at),
    total_collateral: new BigNumber(account.total_collateral),
    free_collateral: new BigNumber(account.free_collateral),
    account_value: new BigNumber(account.account_value),
    portfolio_change: new BigNumber(account.portfolio_change),
    portfolio_change_percentage: new BigNumber(
      account.portfolio_change_percentage,
    ),
    initial_margin_requirement: new BigNumber(
      account.initial_margin_requirement,
    ),
    maintenance_margin_requirement: new BigNumber(
      account.maintenance_margin_requirement,
    ),
  };
}
